import { useEffect, useState } from 'preact/hooks';
import { Link } from 'wouter-preact';
import { api, type ApiErr } from '@/lib/teaching/teacher-api';
import { useAssessmentId, teachHref, loadDataset, datasetOf } from '@/lib/teaching/state';
import { upload, errText, paperPill, type Batch, type ScansView, type TrackerRow, type ScanPaper } from '@/lib/teaching/slice2';
import { Card } from '@/components/teaching/ui';
import { Btn, ErrorLine, Field, inputCls, val } from '@/components/teaching/form';
import { Pill, SetupPage, Tabs, Note, selectCls } from '@/components/teaching/setup';
import { PdfPages } from '@/components/teaching/PdfPages';

// Scans (slice 2 §03): Scan Tracker numbers per class, batch upload,
// reconciliation checklist, page ranges and per-paper status.

const ATTEND_LABEL: Record<string, string> = { present: 'Sat it', absent: 'Absent', catchup: 'Catch-up', exempt: 'Exempt' };
const MAPPING_LABEL: Record<string, { label: string; bg: string }> = {
  none: { label: 'No number yet', bg: '#e2e3e6' },
  waiting: { label: 'AWAITING SCAN', bg: '#e8effb' },
};

export function Scans() {
  const a = useAssessmentId();
  const [view, setView] = useState<ScansView | null>(null);
  const [cls, setCls] = useState<string>('');
  const [err, setErr] = useState<string | null>(null);
  const load = async () => { if (a) setView(await api<ScansView>('GET', `/api/t/assessments/${a}/scans`)); };
  useEffect(() => { setView(null); void load().catch((e) => setErr(errText(e))); }, [a]);
  useEffect(() => { if (view && !view.tracker.some((t) => t.class_id === cls)) setCls(view.tracker[0]?.class_id ?? ''); }, [view]);
  useEffect(() => { if (a) void loadDataset(a); }, [a]);

  const klass = view?.tracker.find((t) => t.class_id === cls);
  const batches = view?.batches.filter((b) => b.class_id === cls) ?? [];
  const ds = a ? datasetOf(a) : undefined;
  const byHand = new Set((ds?.results ?? []).filter((r) => r.mark !== null || r.blank || r.unavailable_reason).map((r) => r.student_id));
  return (
    <SetupPage a={a} title="Scans and upload" actions={a && <Link href={teachHref('/setup/identity', { a })} class="h-9 px-3 rounded-lg border border-[var(--color-border)] bg-[var(--color-card)] text-[13px] grid place-items-center">Identity check</Link>}>
      <ErrorLine error={err} />
      {!view && !err && <div class="text-[13px] text-[var(--color-text-muted)]">Loading…</div>}
      {view && view.tracker.length === 0 && <Card><div class="text-[13px] text-[var(--color-text-muted)]">This test has no classes yet. Add them in the test wizard.</div></Card>}
      {view && klass && (
        <>
          {view.tracker.length > 1 && <Tabs label="Class" value={cls} onChange={setCls} options={view.tracker.map((t) => ({ id: t.class_id, label: t.name }))} />}
          {view.batches.length === 0 && <Note tone="info">No scans yet for this test. Upload a PDF to start. Papers you marked by hand are already in and need no scan.</Note>}
          <div class="grid gap-4 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] items-start">
            <Tracker key={`${cls}:${view.assessment_revision}`} a={a!} view={view} classId={cls} rows={klass.rows} onSaved={setView} scanned={batches.length > 0 || klass.rows.some((r) => !!r.scan_number)} byHand={byHand} />
            <div class="flex flex-col gap-4 min-w-0">
              <UploadForm key={`${cls}:${batches.some((b) => b.kind === 'main')}`} a={a!} classId={cls} defaultPages={view.pages_per_paper ?? 4} hasMain={batches.some((b) => b.kind === 'main')} onDone={load} />
              {batches.map((b) => <BatchCard key={`${b.id}:${b.revision}`} b={b} papers={view.papers.filter((p) => p.batch_id === b.id)} onChanged={load} />)}
            </div>
          </div>
        </>
      )}
    </SetupPage>
  );
}

// ── Tracker ───────────────────────────────────────────────────────────

const canNumber = (r: TrackerRow) => !r.batch_id && r.attendance !== 'absent' && r.attendance !== 'exempt';

function Tracker({ a, view, classId, rows, onSaved, scanned, byHand }: { a: string; view: ScansView; classId: string; rows: TrackerRow[]; onSaved: (v: ScansView) => void; scanned: boolean; byHand: Set<string> }) {
  const init = () => Object.fromEntries(rows.map((r) => [r.student_id, r.scan_number ?? '']));
  const [nums, setNums] = useState<Record<string, string>>(init);
  const [err, setErr] = useState<ApiErr | null>(null);
  const [saving, setSaving] = useState(false);
  const [typing, setTyping] = useState<Set<string>>(new Set());
  const dirty = rows.some((r) => (nums[r.student_id] ?? '') !== (r.scan_number ?? ''));

  function autoNumber() {
    const next = { ...nums };
    const used = (catchup: boolean) => rows.map((r) => next[r.student_id]).filter((n) => n && n.startsWith('C') === catchup).map((n) => Number(n.replace(/^C/, '')));
    for (const catchup of [false, true]) {
      let n = Math.max(0, ...used(catchup));
      for (const r of rows) {
        if (!canNumber(r) || next[r.student_id] || (r.attendance === 'catchup') !== catchup) continue;
        n++;
        next[r.student_id] = (catchup ? 'C' : '') + String(n).padStart(2, '0');
      }
    }
    setNums(next);
  }

  async function save() {
    setErr(null); setSaving(true);
    const entries = rows.map((r) => ({ studentId: r.student_id, scanNumber: r.batch_id ? r.scan_number : canNumber(r) ? (nums[r.student_id]?.trim().toUpperCase() || null) : null }));
    try { onSaved(await api<ScansView>('PUT', `/api/t/assessments/${a}/tracker`, { classId, entries }, view.assessment_revision)); }
    catch (e) { setErr(e as ApiErr); setSaving(false); }
  }
  const fieldErr = (i: number) => err?.fields?.[`entries.${i}.scanNumber`] ?? err?.fields?.[`entries.${i}.studentId`];

  return (
    <Card title="Scan numbers" sub="Before scanning, number the papers in the order they go through the scanner: 01, 02 ... and C01 for catch-ups. Absent and exempt students get no number."
      right={<Btn onClick={autoNumber}>Auto-number</Btn>}>
      <div class="overflow-x-auto -mx-1">
        <table class="w-full text-[12.5px] border-collapse">
          <thead>
            <tr class="text-left text-[11px] uppercase tracking-wider text-[var(--color-text-faint)]">
              <th class="p-1.5">Student</th><th class="p-1.5">Attendance</th><th class="p-1.5">Scan no.</th>{scanned && <th class="p-1.5">Scan status</th>}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => {
              const fe = fieldErr(i);
              const pill = r.batch_id ? paperPill({ batch_id: r.batch_id, mapping_status: r.mapping_status ?? 'unresolved', quarantined: 0 })
                : r.scan_number ? MAPPING_LABEL.waiting : MAPPING_LABEL.none;
              return (
                <tr key={r.student_id} class="border-t border-[var(--color-border)]" data-student={r.name}>
                  <td class="p-1.5"><div class="text-[var(--color-text)]">{r.name}</div>{r.student_code && <div class="text-[11px] text-[var(--color-text-faint)]">{r.student_code}</div>}</td>
                  <td class="p-1.5 text-[var(--color-text-muted)] whitespace-nowrap">{ATTEND_LABEL[r.attendance]}</td>
                  <td class="p-1.5">
                    {!canNumber(r) && !r.batch_id ? <span class="text-[12px] text-[var(--color-text-faint)]" title="No paper to scan">-</span>
                    : !r.batch_id && !r.scan_number && byHand.has(r.student_id) && !nums[r.student_id] && !typing.has(r.student_id) ? (
                      <button type="button" class="text-[12px] text-[var(--color-text-faint)] hover:text-[var(--color-text)]" title="Marks were typed in. Click to give this paper a scan number anyway."
                        onClick={() => setTyping(new Set(typing).add(r.student_id))}>Entered by hand</button>
                    ) : <>
                    <input aria-label={`Scan number ${r.name}`} value={nums[r.student_id] ?? ''} disabled={!canNumber(r)} onInput={(e) => setNums({ ...nums, [r.student_id]: val(e) })}
                      placeholder={canNumber(r) ? '01' : ''} title={fe ?? (r.batch_id ? 'Scanned: change the mapping on the identity screen' : undefined)}
                      class={'w-16 h-8 rounded-md border px-2 text-[13px] bg-[var(--color-bg)] text-[var(--color-text)] disabled:opacity-50 ' + (fe ? 'border-[#b42318]' : 'border-[var(--color-border)]')} />
                    </>}
                    {fe && <div class="text-[11px] text-[#b42318] max-w-[180px]">{fe}</div>}
                  </td>
                  {scanned && <td class="p-1.5"><Pill {...pill} /></td>}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {err && !Object.keys(err.fields ?? {}).some((k) => k.startsWith('entries.')) && <ErrorLine error={errText(err)} />}
      {err?.fields?.entries && <ErrorLine error={err.fields.entries} />}
      <div class="mt-3 flex gap-2 items-center">
        <Btn kind="primary" disabled={!dirty || saving} onClick={save}>Save tracker</Btn>
        {dirty && <span class="text-[12px] text-[var(--color-text-muted)]">Unsaved changes</span>}
      </div>
    </Card>
  );
}

// ── Upload ────────────────────────────────────────────────────────────

function UploadForm({ a, classId, defaultPages, hasMain, onDone }: { a: string; classId: string; defaultPages: number; hasMain: boolean; onDone: () => Promise<void> }) {
  const [file, setFile] = useState<File | null>(null);
  const [kind, setKind] = useState<'main' | 'catchup'>(hasMain ? 'catchup' : 'main');
  const [pages, setPages] = useState(String(defaultPages));
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [key, setKey] = useState(0);

  async function send() {
    if (!file) return;
    setErr(null); setBusy(true);
    const form = new FormData();
    form.append('file', file); form.append('classId', classId); form.append('kind', kind); form.append('pagesPerPaper', pages);
    try { await upload('POST', `/api/t/assessments/${a}/batches`, form); setFile(null); setKey(key + 1); await onDone(); }
    catch (e) { setErr(errText(e)); }
    setBusy(false);
  }

  return (
    <Card title="Upload scans" sub="One PDF per class batch, papers in scan-number order.">
      <div class="flex flex-wrap items-end gap-3">
        <Field label="PDF file" class="min-w-0 flex-1 basis-[220px]">
          <input key={key} type="file" accept="application/pdf,.pdf" onChange={(e) => setFile((e.target as HTMLInputElement).files?.[0] ?? null)}
            class="text-[12.5px] text-[var(--color-text)] min-w-0 max-w-full" />
        </Field>
        <Field label="Batch">
          <select class={selectCls} value={kind} onChange={(e) => setKind(val(e) as 'main' | 'catchup')}>
            <option value="main" disabled={hasMain}>Main</option>
            <option value="catchup">Catch-up</option>
          </select>
        </Field>
        <Field label="Pages per paper">
          <input class={inputCls + ' w-20'} type="number" min={1} max={40} value={pages} onInput={(e) => setPages(val(e))} />
        </Field>
        <Btn kind="primary" disabled={!file || busy} onClick={send}>{busy ? 'Uploading…' : 'Upload'}</Btn>
      </div>
      <ErrorLine error={err} />
    </Card>
  );
}

// ── Batch: reconciliation, ranges, replace ────────────────────────────

function BatchCard({ b, papers, onChanged }: { b: Batch; papers: ScanPaper[]; onChanged: () => Promise<void> }) {
  const r = b.reconciliation;
  const [note, setNote] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [ranges, setRanges] = useState(false);
  const open = r.discrepancies.filter((d) => !d.accepted);
  const run = async (f: () => Promise<unknown>) => { setErr(null); try { await f(); await onChanged(); } catch (e) { setErr(errText(e)); } };

  return (
    <Card title={`${b.kind === 'main' ? 'Main' : 'Catch-up'} batch · ${b.page_count} pages · ${b.pages_per_paper} per paper`}
      sub={`Uploaded ${new Date(b.uploaded_at).toLocaleString('en-AU', { dateStyle: 'medium', timeStyle: 'short' })} · file ${b.file_sha256.slice(0, 8)}`}
      right={<Pill label={r.status === 'reconciled' ? 'RECONCILED' : 'BLOCKED'} bg={r.status === 'reconciled' ? '#d3f0d9' : '#f8cdcd'} />}>
      <div class="flex flex-col gap-3" data-batch={b.id}>
        <div>
          <div class="text-[12px] font-semibold text-[var(--color-text)] mb-1">Reconciliation</div>
          <ul class="flex flex-col gap-1 text-[12.5px]">
            {r.blockers.length === 0 && r.discrepancies.length === 0 && <li class="text-[var(--color-text-muted)]">Every page is in a paper and every tracker number has a paper.</li>}
            {r.blockers.map((x) => (
              <li key={x.key} class="flex gap-2 items-start"><Pill label="BLOCKER" bg="#f8cdcd" /><span class="text-[var(--color-text)]">{x.detail}</span></li>
            ))}
            {r.discrepancies.map((x) => (
              <li key={x.key} class="flex gap-2 items-start">
                <Pill label={x.accepted ? 'ACCEPTED' : x.kind === 'paper_missing' ? 'PAPER MISSING' : 'UNRESOLVED PAPER'} bg={x.accepted ? '#d3f0d9' : '#fde9b8'} />
                <span class="text-[var(--color-text)]">{x.detail}{x.accepted && x.note ? <span class="text-[var(--color-text-muted)]"> · note: {x.note}</span> : null}</span>
              </li>
            ))}
          </ul>
          {r.blockers.length > 0 && <div class="text-[11.5px] text-[var(--color-text-muted)] mt-1">Page problems cannot be accepted: edit the page ranges or replace the file.</div>}
          {open.length > 0 && (
            <div class="mt-2 flex flex-wrap gap-2 items-end">
              <Field label="Note for accepting these" class="flex-1 basis-[220px]">
                <input class={inputCls} value={note} onInput={(e) => setNote(val(e))} placeholder="e.g. Ben's paper was lost; teacher has the original" />
              </Field>
              <Btn disabled={!note.trim()} onClick={() => run(() => api('POST', `/api/t/batches/${b.id}/accept`, { note: note.trim() }))}>Accept with note</Btn>
            </div>
          )}
        </div>

        <div>
          <div class="text-[12px] font-semibold text-[var(--color-text)] mb-1">Papers</div>
          <div class="flex flex-wrap gap-1.5">
            {papers.map((p) => (
              <span key={p.id} class="inline-flex items-center gap-1 rounded-lg border border-[var(--color-border)] px-1.5 py-1 text-[12px] text-[var(--color-text)]" data-paper={p.scan_number}>
                <b>{p.scan_number}</b><span class="text-[var(--color-text-muted)] truncate max-w-[110px]">{p.student_name ?? 'no student'}</span>
                <span class="text-[var(--color-text-faint)]">p{p.first_page}-{p.last_page}</span>
                <Pill {...paperPill(p)} />
              </span>
            ))}
            {papers.length === 0 && <span class="text-[12px] text-[var(--color-text-muted)]">No papers split from this file.</span>}
          </div>
        </div>

        <div class="flex flex-wrap gap-2">
          <Btn onClick={() => setRanges(!ranges)}>{ranges ? 'Close page ranges' : 'Edit page ranges'}</Btn>
          <ReplaceFile b={b} onDone={onChanged} onErr={setErr} />
        </div>
        <ErrorLine error={err} />
        {ranges && <RangeEditor b={b} papers={papers} onSaved={onChanged} />}
      </div>
    </Card>
  );
}

function ReplaceFile({ b, onDone, onErr }: { b: Batch; onDone: () => Promise<void>; onErr: (e: string | null) => void }) {
  const [busy, setBusy] = useState(false);
  async function pick(e: Event) {
    const f = (e.target as HTMLInputElement).files?.[0];
    if (!f) return;
    if (!confirm('Replace this batch file? Marks approved from the old file are voided (kept in the log) and identities must be checked again.')) return;
    onErr(null); setBusy(true);
    const form = new FormData(); form.append('file', f);
    try { await upload('PUT', `/api/t/batches/${b.id}/file`, form); await onDone(); } catch (x) { onErr(errText(x)); }
    setBusy(false);
  }
  return (
    <label class={'h-9 px-3 rounded-lg border border-[var(--color-border)] bg-[var(--color-card)] text-[13px] font-medium grid place-items-center cursor-pointer ' + (busy ? 'opacity-50' : '')}>
      {busy ? 'Replacing…' : 'Replace file'}
      <input type="file" accept="application/pdf,.pdf" class="sr-only" onChange={pick} disabled={busy} />
    </label>
  );
}

function RangeEditor({ b, papers, onSaved }: { b: Batch; papers: ScanPaper[]; onSaved: () => Promise<void> }) {
  const [rows, setRows] = useState(() => papers.map((p) => ({ paperId: p.id, scan: p.scan_number, name: p.student_name, first: String(p.first_page ?? ''), last: String(p.last_page ?? '') })));
  const [excluded, setExcluded] = useState(b.excluded_pages.map((e) => ({ page: String(e.page), note: e.note })));
  const [err, setErr] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState<string | null>(papers[0]?.id ?? null);

  async function save() {
    setErr(null); setSaving(true);
    try {
      await api('PUT', `/api/t/batches/${b.id}/ranges`, {
        expectedRevision: b.revision,
        papers: rows.map((r) => ({ paperId: r.paperId, first: Number(r.first), last: Number(r.last) })),
        excluded: excluded.filter((e) => e.page.trim()).map((e) => ({ page: Number(e.page), note: e.note.trim() })),
      });
      await onSaved();
    } catch (e) { setErr(errText(e)); setSaving(false); }
  }
  const set = (i: number, patch: Partial<(typeof rows)[number]>) => setRows(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const shown = rows.find((r) => r.paperId === preview);

  return (
    <div class="rounded-xl border border-[var(--color-border)] bg-[var(--color-elevated)] p-3 flex flex-col gap-3">
      <Note tone="warn">Changing a paper's pages voids its approved marks (kept in the log) and sends it back to the identity check.</Note>
      <div class="grid gap-3 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        <div class="flex flex-col gap-1.5 min-w-0">
          {rows.map((r, i) => (
            <div key={r.paperId} class={'flex flex-wrap items-center gap-2 rounded-lg px-2 py-1 ' + (preview === r.paperId ? 'bg-[var(--color-card)]' : '')}>
              <button type="button" class="text-[12.5px] font-semibold w-10 text-left text-[var(--color-accent)]" onClick={() => setPreview(r.paperId)}>{r.scan}</button>
              <span class="text-[12px] text-[var(--color-text-muted)] w-24 truncate">{r.name ?? 'no student'}</span>
              <input aria-label={`First page ${r.scan}`} class={inputCls + ' w-16'} type="number" min={1} max={b.page_count} value={r.first} onInput={(e) => set(i, { first: val(e) })} />
              <span class="text-[12px] text-[var(--color-text-faint)]">to</span>
              <input aria-label={`Last page ${r.scan}`} class={inputCls + ' w-16'} type="number" min={1} max={b.page_count} value={r.last} onInput={(e) => set(i, { last: val(e) })} />
            </div>
          ))}
          <div class="text-[12px] font-semibold text-[var(--color-text)] mt-2">Excluded pages (blank or cover)</div>
          {excluded.map((x, i) => (
            <div key={i} class="flex flex-wrap gap-2 items-center">
              <input aria-label={`Excluded page ${i + 1}`} class={inputCls + ' w-16'} type="number" min={1} max={b.page_count} value={x.page} onInput={(e) => setExcluded(excluded.map((y, j) => (j === i ? { ...y, page: val(e) } : y)))} />
              <input aria-label={`Excluded note ${i + 1}`} class={inputCls + ' flex-1 min-w-[120px]'} value={x.note} placeholder="Why (e.g. blank cover)" onInput={(e) => setExcluded(excluded.map((y, j) => (j === i ? { ...y, note: val(e) } : y)))} />
              <Btn kind="danger" onClick={() => setExcluded(excluded.filter((_, j) => j !== i))}>Remove</Btn>
            </div>
          ))}
          <div><Btn onClick={() => setExcluded([...excluded, { page: '', note: '' }])}>Exclude a page</Btn></div>
        </div>
        <div class="min-w-0">
          {shown && Number(shown.first) >= 1 && Number(shown.last) >= Number(shown.first) && Number(shown.last) <= b.page_count ? (
            <PdfPages batchId={b.id} fileKey={b.file_sha256} first={Number(shown.first)} last={Number(shown.last)} width={120} />
          ) : <div class="text-[12px] text-[var(--color-text-muted)]">Pick a paper to see its pages.</div>}
        </div>
      </div>
      <ErrorLine error={err} />
      <div><Btn kind="primary" disabled={saving} onClick={save}>Save page ranges</Btn></div>
    </div>
  );
}
